import type React from "react"
import { useEffect, useRef, useState, useCallback } from "react"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { useTranslation } from "react-i18next"

interface LocationPickerProps {
  onLocationSelected: (lat: number, lng: number) => void
  onClose: () => void
}

const LocationPicker: React.FC<LocationPickerProps> = ({ onLocationSelected, onClose }) => {
  const { t } = useTranslation()
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const marker = useRef<mapboxgl.Marker | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  const [lat, setLat] = useState<number | null>(null)
  const [zoom, setZoom] = useState(15)
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null)

  const initializeMap = useCallback(
    (longitude: number, latitude: number) => {
      if (map.current) return // Initialize map only once

      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ""
      map.current = new mapboxgl.Map({
        container: mapContainer.current!,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [longitude, latitude],
        zoom: zoom,
      })

      map.current.on("load", () => {
        if (map.current) {
          map.current.resize()
        }
      })

      map.current.on("moveend", () => {
        if (map.current) {
          const center = map.current.getCenter()
          setLng(Number(center.lng.toFixed(4)))
          setLat(Number(center.lat.toFixed(4)))
          setZoom(Number(map.current.getZoom().toFixed(2)))
        }
      })

      map.current.on("click", (e) => {
        const { lng, lat } = e.lngLat
        setSelectedLocation({ lat, lng })
        if (marker.current) {
          marker.current.setLngLat([lng, lat])
        } else {
          marker.current = new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map.current!)
        }
      })

      marker.current = new mapboxgl.Marker().setLngLat([longitude, latitude]).addTo(map.current)
      setSelectedLocation({ lat: latitude, lng: longitude })
    },
    [zoom],
  )

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords
          setLng(longitude)
          setLat(latitude)
          initializeMap(longitude, latitude)
        },
        () => {
          // Fallback to a default location if geolocation is not available
          setLng(-70)
          setLat(42)
          initializeMap(-70, 42)
        },
      )
    } else {
      // Fallback to a default location if geolocation is not supported
      setLng(-70)
      setLat(42)
      initializeMap(-70, 42)
    }

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
      if (marker.current) {
        marker.current.remove()
        marker.current = null
      }
    }
  }, [initializeMap])

  useEffect(() => {
    const resizeMap = () => {
      if (map.current) {
        map.current.resize()
      }
    }

    window.addEventListener("resize", resizeMap)
    return () => window.removeEventListener("resize", resizeMap)
  }, [])

  const handleSendLocation = () => {
    if (selectedLocation) {
      onLocationSelected(selectedLocation.lat, selectedLocation.lng)
    }
  }

  const handleClose = () => {
    if (map.current) {
      map.current.remove()
      map.current = null
    }
    if (marker.current) {
      marker.current.remove()
      marker.current = null
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-4 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <h2 className="text-xl font-bold mb-4">{t("select_location")}</h2>
        <div ref={mapContainer} className="flex-grow w-full mb-4" style={{ minHeight: "300px" }} />
        <div className="flex justify-between items-center">
          <div className="text-sm">
            {t("longitude")}: {lng?.toFixed(4) || t("loading")} | {t("latitude")}: {lat?.toFixed(4) || t("loading")} |{" "}
            {t("zoom")}: {zoom}
          </div>
          <div>
            <button
              onClick={handleSendLocation}
              disabled={!selectedLocation}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("send")}
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LocationPicker

